import React from "react";
import { useTranslation } from "react-i18next";
import { Check, AlertCircle } from "lucide-react";

const COLORS = [
    { name: "Limestone (bg)", value: "#FAF9F6", fg: "text-ink-800" },
    { name: "Mudbrick (alt)", value: "#F0ECE1", fg: "text-ink-800" },
    { name: "Madder (CTA)", value: "#8A1E12", fg: "text-white" },
    { name: "Red Sea Teal", value: "#0D525C", fg: "text-white" },
    { name: "Souq Yellow", value: "#E59500", fg: "text-ink-900" },
    { name: "Ink (text)", value: "#1A1C1D", fg: "text-white" },
    { name: "Ink secondary", value: "#4A5568", fg: "text-white" },
    { name: "Border", value: "#E2E0D8", fg: "text-ink-800" },
];

const RADII = [
    { name: "Buttons", value: "12px", cls: "rounded-btn" },
    { name: "Cards", value: "16px", cls: "rounded-card" },
    { name: "Hero band", value: "20px", cls: "rounded-hero" },
    { name: "Pills", value: "999px", cls: "rounded-full" },
];

const SHADOWS = [
    { name: "Hair", cls: "shadow-hair" },
    { name: "Lift", cls: "shadow-lift" },
    { name: "Pop", cls: "shadow-pop" },
    { name: "Madder", cls: "shadow-madder" },
    { name: "Red Sea", cls: "shadow-redsea" },
    { name: "Souq", cls: "shadow-souq" },
];

export default function StyleGuide() {
    const { t } = useTranslation();
    return (
        <main className="min-h-screen bg-limestone-100 pt-28" data-testid="style-guide-page">
            <div className="mx-auto max-w-[1320px] px-5 pb-24 lg:px-8">
                <header className="max-w-2xl border-b-2 border-ink-800 pb-10">
                    <p className="eyebrow">NourNet · v5 Architectural Hybrid</p>
                    <h1 className="section-headline mt-5">{t("styleguide.title")}</h1>
                    <p className="body-prose mt-6">{t("styleguide.subtitle")}</p>
                </header>

                <Section title={t("styleguide.colors")} anchor="colors">
                    <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
                        {COLORS.map((c) => (
                            <div key={c.name} className="bento-card overflow-hidden" data-testid={`swatch-${c.name}`}>
                                <div
                                    className={`flex h-28 items-end p-4 font-mono text-[13px] font-bold num-ltr ${c.fg}`}
                                    style={{ background: c.value }}
                                >
                                    {c.value}
                                </div>
                                <div className="p-4 text-[14px] font-bold text-ink-800">{c.name}</div>
                            </div>
                        ))}
                    </div>
                </Section>

                <Section title={t("styleguide.typography")} anchor="typography">
                    <div className="space-y-8">
                        <Row label="Display · Outfit 800" meta="56-112px · -0.035em">
                            <p className="display-headline">A registry, refined.</p>
                        </Row>
                        <Row label="H2 · Outfit 700" meta="38-64px · -0.025em">
                            <p className="section-headline">Everything Saudi business needs.</p>
                        </Row>
                        <Row label="Eyebrow · Outfit 700 uppercase" meta="14px · 0.18em">
                            <p className="eyebrow">Honest pricing</p>
                        </Row>
                        <Row label="Body · Outfit 500" meta="19px · 1.7">
                            <p className="body-prose max-w-2xl">
                                All plans include Saudi-hosted infrastructure, 2FA, and priority business-hours support.
                            </p>
                        </Row>
                        <Row label="Mono · JetBrains" meta="for zones, IDs, prices">
                            <p className="font-mono text-[16px] font-semibold text-ink-800 num-ltr tabular-nums">
                                185.199.108.153 · 300 · ns1.nour.net
                            </p>
                        </Row>
                        <Row label="Arabic · IBM Plex Sans Arabic" meta="500 / 700">
                            <p className="font-arabic text-[24px] font-bold text-ink-800" dir="rtl">
                                نورنت — منصة المملكة الموحّدة.
                            </p>
                        </Row>
                    </div>
                </Section>

                <Section title={t("styleguide.radii")} anchor="radii">
                    <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
                        {RADII.map((r) => (
                            <div key={r.name} className="bento-card p-6 text-center">
                                <div className={`mx-auto mb-5 h-20 w-20 border-2 border-ink-800 bg-souq-200 ${r.cls}`} />
                                <p className="text-[15px] font-bold text-ink-800">{r.name}</p>
                                <p className="font-mono text-[13px] font-semibold text-ink-600 num-ltr">{r.value}</p>
                            </div>
                        ))}
                    </div>
                </Section>

                <Section title={t("styleguide.shadows")} anchor="shadows">
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                        {SHADOWS.map((s) => (
                            <div key={s.name} className={`bento-card p-8 ${s.cls}`}>
                                <p className="text-[15px] font-bold text-ink-800">{s.name}</p>
                                <p className="mt-1 font-mono text-[12px] font-semibold text-ink-500">{s.cls}</p>
                            </div>
                        ))}
                    </div>
                </Section>

                <Section title={t("styleguide.states")} anchor="states">
                    <p className="body-prose max-w-2xl">{t("styleguide.states_desc")}</p>
                    <div className="bento-card mt-6 p-7">
                        <div className="flex flex-wrap items-center gap-3">
                            <button className="h-12 rounded-btn bg-madder-500 px-5 text-[15px] font-bold text-white">
                                {t("styleguide.buttonDefault")}
                            </button>
                            <button className="h-12 rounded-btn bg-madder-600 px-5 text-[15px] font-bold text-white">
                                {t("styleguide.buttonHover")}
                            </button>
                            <button
                                className="h-12 rounded-btn bg-madder-500 px-5 text-[15px] font-bold text-white"
                                style={{ outline: "3px solid #8A1E12", outlineOffset: "3px" }}
                            >
                                {t("styleguide.buttonFocus")}
                            </button>
                            <button disabled className="h-12 rounded-btn bg-madder-500 px-5 text-[15px] font-bold text-white opacity-50">
                                {t("styleguide.buttonDisabled")}
                            </button>
                        </div>
                        <div className="mt-6 flex flex-wrap items-center gap-3">
                            <span className="inline-flex items-center gap-1.5 rounded-[6px] bg-redsea-500 px-3 py-1.5 text-[12px] font-bold text-white">
                                <Check className="h-3.5 w-3.5" strokeWidth={3} />
                                Success
                            </span>
                            <span className="inline-flex items-center gap-1.5 rounded-[6px] bg-ink-800 px-3 py-1.5 text-[12px] font-bold text-white">
                                Badge · Ink
                            </span>
                            <span className="inline-flex items-center gap-1.5 rounded-[6px] bg-souq-400 px-3 py-1.5 text-[12px] font-bold text-ink-900">
                                Badge · Souq
                            </span>
                            <span className="inline-flex items-center gap-1.5 rounded-[6px] bg-madder-50 px-3 py-1.5 text-[12px] font-bold text-madder-700">
                                <AlertCircle className="h-3.5 w-3.5" />
                                Error
                            </span>
                        </div>
                    </div>
                </Section>
            </div>
        </main>
    );
}

const Section = ({ title, anchor, children }) => (
    <section id={anchor} className="mt-20 scroll-mt-24">
        <h2 className="mb-8 font-display text-[32px] font-bold tracking-tight text-ink-800">{title}</h2>
        {children}
    </section>
);

const Row = ({ label, meta, children }) => (
    <div className="border-t-2 border-ink-200 pt-5">
        <div className="mb-3 flex items-center gap-3">
            <p className="font-mono text-[12px] font-bold uppercase tracking-[0.16em] text-ink-700">{label}</p>
            <p className="font-mono text-[11px] font-semibold text-ink-500 num-ltr">{meta}</p>
        </div>
        {children}
    </div>
);
